import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { ImageUpload } from './image-upload'
import { AlertCircle, CheckCircle2, Image as ImageIcon, X, Loader2 } from 'lucide-react'
import { useLogo } from '@/hooks/use-logo'
import { useImageUpload } from '@/hooks/use-image-upload'

export function LogoManager() {
  const { logoUrl, loading, error, updateLogo, refetch } = useLogo()
  const { uploadImage, uploading, deleteImage } = useImageUpload()
  const [saving, setSaving] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [statusType, setStatusType] = useState<'error' | 'success' | ''>('')
  const [previewUrl, setPreviewUrl] = useState<string | null>(logoUrl || null)

  const handleImageChange = async (imageUrl: string) => {
    setPreviewUrl(imageUrl || null)
  }

  const handleSave = async () => {
    if (!previewUrl) {
      setStatusType('error')
      setStatusMessage('Please upload a logo image before saving.')
      return
    }

    setSaving(true)
    setStatusMessage('')
    setStatusType('')

    const success = await updateLogo(previewUrl)

    if (success) {
      setStatusType('success')
      setStatusMessage('Logo updated successfully.')
      await refetch()
    } else {
      setStatusType('error')
      setStatusMessage('Failed to update logo. Please try again.')
    }

    setSaving(false)

    // Clear message after 5 seconds
    setTimeout(() => {
      setStatusMessage('')
      setStatusType('')
    }, 5000)
  }

  const handleRemove = async () => {
    if (!logoUrl) return

    setSaving(true)
    setStatusMessage('')
    setStatusType('')

    // Delete from storage if it's a Supabase storage URL
    if (logoUrl.includes('supabase.co/storage')) {
      await deleteImage(logoUrl)
    }

    const success = await updateLogo(null)

    if (success) {
      setPreviewUrl(null)
      setStatusType('success')
      setStatusMessage('Logo removed successfully.')
      await refetch()
    } else {
      setStatusType('error')
      setStatusMessage('Failed to remove logo. Please try again.')
    }

    setSaving(false)

    setTimeout(() => {
      setStatusMessage('')
      setStatusType('')
    }, 5000)
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent">
            Site Logo
          </h2>
          <p className="text-slate-600 text-lg">
            Upload and manage the logo image displayed in the navigation bar.
          </p>
        </div>
      </div>

      {statusMessage && (
        <Alert
          className={
            statusType === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : undefined
          }
          variant={statusType === 'error' ? 'destructive' : 'default'}
        >
          {statusType === 'success' ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>{statusMessage}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Logo Management
          </CardTitle>
          <CardDescription>
            Upload a logo image that will be displayed in the site header. Recommended size: 40x40px to 80x80px. Supported formats: PNG, JPG, SVG.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-3" />
              Loading logo...
            </div>
          ) : (
            <div className="space-y-6">
              {/* Current Logo Preview */}
              {(previewUrl || logoUrl) && (
                <div className="p-6 rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-slate-100">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Current Logo Preview</h3>
                  <div className="flex items-center gap-4">
                    <div className="p-4 rounded-xl bg-white border-2 border-slate-200 shadow-sm flex items-center justify-center">
                      <img
                        src={previewUrl || logoUrl || ''}
                        alt="Site Logo"
                        className="w-16 h-16 md:w-20 md:h-20 object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-600 mb-2">
                        This is how your logo appears in the navigation bar (56px on mobile, 64px on desktop).
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRemove}
                        disabled={saving}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Remove Logo
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Logo Upload */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800">
                  {logoUrl ? 'Update Logo' : 'Upload Logo'}
                </h3>
                <ImageUpload
                  currentImage={previewUrl || undefined}
                  onImageChange={handleImageChange}
                  folder="logos"
                  label="Logo Image"
                  description="Upload a logo image (PNG, JPG, or SVG). Recommended: square image, 40-80px width."
                />
              </div>

              {/* Save Button */}
              {previewUrl && (
                <div className="flex items-center gap-4 pt-4 border-t">
                  <Button
                    onClick={handleSave}
                    disabled={saving || uploading}
                    className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Logo'
                    )}
                  </Button>
                  {previewUrl !== logoUrl && (
                    <p className="text-sm text-muted-foreground">
                      Click "Save Logo" to apply changes
                    </p>
                  )}
                </div>
              )}

              {/* Info Box */}
              <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-900">Logo Guidelines:</h3>
                <ul className="space-y-2 text-sm text-blue-800 list-disc list-inside">
                  <li>Recommended size: 56x56px to 64x64px (square format works best)</li>
                  <li>Supported formats: PNG, JPG, SVG</li>
                  <li>For best results, use a transparent background (PNG or SVG)</li>
                  <li>The logo will be displayed prominently in the header next to the site name</li>
                  <li>Logo displays at 56px (mobile) and 64px (desktop) for optimal visibility</li>
                  <li>If no logo is uploaded, the default icon will be displayed</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
